import React, { useState, useEffect, useCallback } from 'react';
import apiService from '../apiService';
import { useLiveRefresh } from '../liveRefresh';

const STATUS_META = {
  draft: { label: 'Brouillon', bg: '#F4F5F7', color: '#414852' },
  pending: { label: 'En validation', bg: '#fef3c7', color: '#C9350F' },
  published: { label: 'Publié', bg: '#ECFDF5', color: '#166534' },
  soldout: { label: 'Complet', bg: '#FEE9E2', color: '#C9350F' },
  cancelled: { label: 'Annulé', bg: '#fee2e2', color: '#991b1b' },
  past: { label: 'Terminé', bg: '#F4F5F7', color: '#9AA0A8' },
};
const fmt = (d) => { try { return new Date(d).toLocaleString('fr-FR', { dateStyle: 'medium', timeStyle: 'short' }); } catch { return '—'; } };
const money = (n) => `${(n || 0).toLocaleString('fr-FR')} CDF`;

export default function EventsAdminPage() {
  const [status, setStatus] = useState('');
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiService.get(`/admin/events${status ? `?status=${status}` : ''}`, { userType: 'admin' });
      setEvents(res?.success ? (res.events || []) : []);
    } catch (e) { setEvents([]); }
    finally { setLoading(false); }
  }, [status]);

  useEffect(() => { load(); }, [load]);
  useLiveRefresh(() => load(), ['event_*']);

  const openDetail = async (id) => {
    setDetailLoading(true); setDetail({ loading: true });
    try {
      const res = await apiService.get(`/admin/events/${id}`, { userType: 'admin' });
      if (res?.success) setDetail(res); else { setDetail(null); alert(res?.error || 'Erreur'); }
    } catch (e) { setDetail(null); alert(e?.error || 'Erreur'); }
    finally { setDetailLoading(false); }
  };

  const act = async (id, action, body, closeModal) => {
    setBusy(id + action);
    try {
      await apiService.post(`/admin/events/${id}/${action}`, body || {}, { userType: 'admin' });
      if (closeModal) setDetail(null);
      await load();
    } catch (e) { alert(e?.error || e?.data?.error || 'Action impossible'); }
    finally { setBusy(null); }
  };

  const approve = (ev, fromModal) => act(ev.event_id, 'approve', null, fromModal);
  const reject = (ev, fromModal) => { const reason = window.prompt('Motif du rejet ?', 'Non conforme'); if (reason !== null) act(ev.event_id, 'reject', { reason }, fromModal); };
  const cancel = (ev, fromModal) => { if (window.confirm(`Annuler « ${ev.title} » ? Les acheteurs seront remboursés sur leur solde.`)) act(ev.event_id, 'cancel', null, fromModal); };
  const unlock = (ev, fromModal) => { if (window.confirm(`Débloquer le retrait des fonds pour « ${ev.title} » (avant l'événement) ?`)) act(ev.event_id, 'unlock-payout', null, fromModal); };
  const reactivate = (ev, fromModal) => { if (window.confirm(`Réactiver « ${ev.title} » ? Il redeviendra publié. Les billets déjà remboursés restent remboursés (leurs acheteurs devront racheter).`)) act(ev.event_id, 'reactivate', null, fromModal); };

  const ActionButtons = ({ ev, fromModal }) => (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
      {ev.status === 'pending' && (
        <>
          <button style={btn('#10B981')} disabled={busy} onClick={() => approve(ev, fromModal)}>Valider</button>
          <button style={btn('#5C636E')} disabled={busy} onClick={() => reject(ev, fromModal)}>Rejeter</button>
        </>
      )}
      {['published', 'soldout'].includes(ev.status) && (
        <>
          <button style={btn('#C9350F')} disabled={busy} onClick={() => unlock(ev, fromModal)}>Débloquer fonds</button>
          <button style={btn('#dc2626')} disabled={busy} onClick={() => cancel(ev, fromModal)}>{fromModal ? "Annuler l'événement" : 'Annuler'}</button>
        </>
      )}
      {ev.status === 'pending' && ev.cancel_requested && (
        <button style={btn('#dc2626')} disabled={busy} onClick={() => cancel(ev, fromModal)}>{fromModal ? "Annuler l'événement" : 'Annuler'}</button>
      )}
      {ev.status === 'cancelled' && (
        <button style={btn('#10B981')} disabled={busy} onClick={() => reactivate(ev, fromModal)}>{fromModal ? "Réactiver l'événement" : 'Réactiver'}</button>
      )}
    </div>
  );

  return (
    <div className="dashboard-content">
      <div className="page-header">
        <h1 className="page-title">🎫 Événements (Billetterie)</h1>
        <div className="page-actions">
          <select className="filter-select" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">Tous</option>
            <option value="pending">En validation</option>
            <option value="published">Publiés</option>
            <option value="soldout">Complets</option>
            <option value="cancelled">Annulés</option>
            <option value="past">Terminés</option>
            <option value="draft">Brouillons</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="loading-spinner"><div className="spinner"></div><p>Chargement…</p></div>
      ) : events.length === 0 ? (
        <p style={{ color: '#9AA0A8', padding: '2rem', textAlign: 'center' }}>Aucun événement pour ce filtre.</p>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr><th>Événement</th><th>Organisateur</th><th>Ville</th><th>Dates</th><th>Billets</th><th>Statut</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {events.map(ev => {
                const st = STATUS_META[ev.status] || STATUS_META.draft;
                return (
                  <tr key={ev.event_id}>
                    <td>
                      <div onClick={() => openDetail(ev.event_id)} style={{ cursor: 'pointer' }}>
                        <div style={{ fontWeight: 600, color: '#C9350F', textDecoration: 'underline' }}>{ev.title}</div>
                        {!!ev.venue?.name && <div style={{ fontSize: '0.8rem', color: '#5C636E' }}>📍 {ev.venue.name}</div>}
                        {ev.cancel_requested && ev.status !== 'cancelled' && <div style={{ fontSize: '0.78rem', color: '#B91C1C', fontWeight: 600 }}>⚠️ Annulation demandée</div>}
                      </div>
                    </td>
                    <td style={{ fontSize: '0.85rem', color: '#414852' }}>{ev.organizer_name || '—'}</td>
                    <td style={{ fontSize: '0.85rem', color: '#414852' }}>{ev.city_slug}</td>
                    <td style={{ fontSize: '0.82rem', color: '#414852' }}>{fmt(ev.starts_at)}<br />→ {fmt(ev.ends_at)}</td>
                    <td style={{ fontSize: '0.85rem', color: '#1E2229', fontWeight: 600 }}>{ev.tickets_sold || 0}/{ev.tickets_quota || 0}</td>
                    <td><span style={{ background: st.bg, color: st.color, padding: '3px 10px', borderRadius: 999, fontSize: '0.75rem', fontWeight: 700, whiteSpace: 'nowrap' }}>{st.label}</span></td>
                    <td><ActionButtons ev={ev} fromModal={false} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {detail && (
        <div style={overlay} onClick={() => setDetail(null)}>
          <div style={modalCard} onClick={(e) => e.stopPropagation()}>
            {detail.loading || detailLoading ? (
              <div style={{ padding: 40, textAlign: 'center', color: '#5C636E' }}>Chargement…</div>
            ) : (
              <EventDetailModal detail={detail} onClose={() => setDetail(null)} ActionButtons={ActionButtons} />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function EventDetailModal({ detail, onClose, ActionButtons }) {
  const ev = detail.event; const s = detail.stats || {}; const org = detail.organizer;
  const st = STATUS_META[ev.status] || STATUS_META.draft;
  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '18px 20px', borderBottom: '1px solid #E9EBEF' }}>
        <div>
          <h3 style={{ margin: 0, fontSize: '1.2rem', color: '#1E2229' }}>{ev.title}</h3>
          <span style={{ background: st.bg, color: st.color, padding: '2px 10px', borderRadius: 999, fontSize: '0.72rem', fontWeight: 700 }}>{st.label}</span>
        </div>
        <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.6rem', cursor: 'pointer', color: '#9AA0A8', lineHeight: 1 }}>×</button>
      </div>

      <div style={{ padding: 20, overflowY: 'auto' }}>
        <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap' }}>
          {ev.poster && <img src={ev.poster} alt="" style={{ width: 140, height: 187, objectFit: 'cover', borderRadius: 10, border: '1px solid #E9EBEF' }} />}
          <div style={{ flex: 1, minWidth: 220, fontSize: '0.9rem', color: '#2C313A', lineHeight: 1.7 }}>
            <div><strong>Organisateur :</strong> {org?.name || '—'}{org?.phone ? ` · ${org.phone}` : ''}</div>
            <div><strong>Ville :</strong> {ev.city_slug}</div>
            <div><strong>Lieu :</strong> {ev.venue?.name || '—'}{ev.venue?.address ? ` — ${ev.venue.address}` : ''}</div>
            <div><strong>Début :</strong> {fmt(ev.starts_at)}</div>
            <div><strong>Fin :</strong> {fmt(ev.ends_at)}</div>
            {!!ev.description && <div style={{ marginTop: 6, color: '#414852' }}>{ev.description}</div>}
          </div>
        </div>

        {/* Finances */}
        <h4 style={sectionTitle}>💰 Finances</h4>
        <div style={statGrid}>
          <Stat label="Recette brute" value={money(s.gross)} />
          <Stat label={`Commission (${Math.round((s.commission_rate || 0) * 100)}%)`} value={money(s.commission)} color="#C9350F" />
          <Stat label="Net organisateur" value={money(s.net)} color="#166534" />
          <Stat label="Débloqué (retirable)" value={money(s.released_net)} />
          <Stat label="En attente" value={money(s.pending_net)} color="#C9350F" />
          {s.reversed_net > 0 && <Stat label="Annulé/remboursé" value={money(s.reversed_net)} color="#991b1b" />}
        </div>

        {/* Billets */}
        <h4 style={sectionTitle}>🎟️ Billets</h4>
        <div style={statGrid}>
          <Stat label="Vendus" value={s.sold_tickets || 0} />
          <Stat label="Entrées scannées" value={s.scanned_in || 0} color="#166534" />
          <Stat label="Remboursés" value={s.refunded_tickets || 0} color="#991b1b" />
          <Stat label="Commandes payées" value={s.orders_paid || 0} />
        </div>

        {/* Catégories */}
        <h4 style={sectionTitle}>Catégories</h4>
        <table className="data-table" style={{ width: '100%' }}>
          <thead><tr><th>Nom</th><th>Prix</th><th>Vendus</th><th>Restant</th></tr></thead>
          <tbody>
            {(detail.tiers || []).map(t => (
              <tr key={t.tier_id}>
                <td>{t.name}{t.active === false ? ' (inactive)' : ''}</td>
                <td>{money(t.price)}</td>
                <td>{t.sold || 0}/{t.quota}</td>
                <td>{t.available}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ padding: '14px 20px', borderTop: '1px solid #E9EBEF', display: 'flex', justifyContent: 'flex-end' }}>
        <ActionButtons ev={ev} fromModal={true} />
      </div>
    </>
  );
}

function Stat({ label, value, color }) {
  return (
    <div style={{ background: '#FFFFFF', border: '1px solid #E9EBEF', borderRadius: 10, padding: '10px 12px' }}>
      <div style={{ fontSize: '0.7rem', color: '#5C636E', textTransform: 'uppercase', letterSpacing: 0.3 }}>{label}</div>
      <div style={{ fontSize: '1.05rem', fontWeight: 800, color: color || '#1E2229', marginTop: 3 }}>{value}</div>
    </div>
  );
}

const btn = (color) => ({ background: color, color: '#fff', border: 'none', borderRadius: 8, padding: '6px 12px', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer' });
const overlay = { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 };
const modalCard = { background: '#fff', borderRadius: 14, width: 'min(760px, 96vw)', maxHeight: '92vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' };
const sectionTitle = { margin: '18px 0 8px', fontSize: '0.95rem', color: '#1E2229' };
const statGrid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 10 };
